"use client";

import { useAuth } from "@/context/AuthContext";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, Loader2, Lock, Mail, X } from "lucide-react";
import { useState } from "react";

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
    const [view, setView] = useState<'login' | 'signup' | 'forgot'>('login');
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const { signIn, signUp, resetPassword } = useAuth();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const isAgent = (e as any).agentInvoked || (e.nativeEvent as any)?.agentInvoked;
        if (isAgent && view === 'signup') {
            const promise = (async () => {
                await signUp(email, password);
                return { success: true };
            })();
            const respond = (e as any).respondWith || (e.nativeEvent as any)?.respondWith;
            if (typeof respond === "function") {
                respond.call(e.nativeEvent || e, promise);
            }
            onClose();
            return;
        }

        setError("");
        setLoading(true);

        try {
            if (view === 'login') {
                await signIn(email, password);
                onClose();
            } else if (view === 'signup') {
                await signUp(email, password);
                onClose();
            } else if (view === 'forgot') {
                await resetPassword(email);
                setError("Reset link sent! Check your email.");
                return; // Don't close modal, show success message
            }
        } catch (err: any) {
            console.error("Auth error:", err);
            // Interactive error handling
            if (err.code === 'auth/email-already-in-use') {
                setError("Email already in use. Try logging in.");
            } else if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
                setError("Invalid email or password.");
            } else if (err.code === 'auth/weak-password') {
                setError("Password should be at least 6 characters.");
            } else if (err.code === 'auth/user-not-found') {
                setError("No user found with this email.");
            } else {
                setError(err.message || "Authentication failed.");
            }
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                />

                {/* Modal */}
                <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 20 }}
                    className="relative bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden"
                >
                    {/* Header */}
                    <div className="px-8 pt-8 pb-4">
                        <div className="flex justify-between items-start">
                            <div>
                                <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600">
                                    {view === 'login' ? "Welcome Back" : view === 'signup' ? "Join the Movement" : "Reset Password"}
                                </h2>
                                <p className="text-gray-500 mt-1 text-sm">
                                    {view === 'login'
                                        ? "Sign in to continue refining your ideas."
                                        : view === 'signup'
                                            ? "Create an account to unlock unlimited potential."
                                            : "Enter your email to receive a recovery link."}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                aria-label="Close modal"
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="h-5 w-5 text-gray-500" />
                            </button>
                        </div>
                    </div>

                    {/* Form */}
                    <form
                        onSubmit={handleSubmit}
                        className="px-8 pb-8 space-y-4"
                        toolname={view === 'signup' ? 'sign-up' : undefined}
                        tooldescription={view === 'signup' ? 'Register a new user account with an email and password.' : undefined}
                        toolautosubmit={view === 'signup' ? "true" : undefined}
                    >
                        {error && (
                            <div className={`text-sm p-3 rounded-xl flex items-center gap-2 ${error.includes("sent") ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>
                                <AlertCircle className="h-4 w-4 shrink-0" />
                                {error}
                            </div>
                        )}

                        <div className="space-y-4">
                            <div className="relative group">
                                <Mail className="absolute left-3 top-3.5 h-5 w-5 text-gray-400 group-focus-within:text-black transition-colors" />
                                <input
                                    type="email"
                                    placeholder="Email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-gramm="false"
                                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:ring-0 outline-none transition-all"
                                    required
                                    toolparamdescription={view === 'signup' ? "The email address to register" : undefined}
                                />
                            </div>

                            {view !== 'forgot' && (
                                <div className="space-y-1">
                                    <div className="relative group">
                                        <Lock className="absolute left-3 top-3.5 h-5 w-5 text-gray-400 group-focus-within:text-black transition-colors" />
                                        <input
                                            type="password"
                                            placeholder="Password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            data-gramm="false"
                                            className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:ring-0 outline-none transition-all"
                                            required
                                            minLength={6}
                                            toolparamdescription={view === 'signup' ? "The password to register (minimum 6 characters)" : undefined}
                                        />
                                    </div>
                                    {view === 'login' && (
                                        <div className="flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => { setView('forgot'); setError(""); }}
                                                className="text-xs text-gray-500 hover:text-black transition-colors"
                                            >
                                                Forgot Password?
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-black text-white font-bold py-3.5 rounded-xl hover:bg-gray-800 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                    <span className="sr-only">Processing...</span>
                                </>
                            ) : (
                                view === 'login' ? "Sign In" : view === 'signup' ? "Create Account" : "Send Reset Link"
                            )}
                        </button>

                        <div className="text-center pt-2">
                            <button
                                type="button"
                                onClick={() => {
                                    if (view === 'forgot') setView('login');
                                    else setView(view === 'login' ? 'signup' : 'login');
                                    setError("");
                                }}
                                className="text-sm text-gray-500 hover:text-black transition-colors"
                            >
                                {view === 'login' ? (
                                    <>Don't have an account? <span className="font-bold underline">Sign up</span></>
                                ) : view === 'signup' ? (
                                    <>Already have an account? <span className="font-bold underline">Sign in</span></>
                                ) : (
                                    <>Remember your password? <span className="font-bold underline">Back to Login</span></>
                                )}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
